import { useMemo } from "react";

import { PatientRefIntervenorWithDetails } from "@/Common/Api/Patient/Dto/PatientRefIntervenorWithDetails";
import { Modal } from "@/Common/Components/Modals/Modal";
import { usePartialState } from "@/Common/CustomHooks/usePartialState";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ObjectHelper } from "@/Common/Helpers/ObjectHelper";
import { PatientRefIntervenorWithDetailsModel } from "@/Common/Models/Patient/PatientRefIntervenorWithDetailsModel";
import { IPatientCliniciansModel } from "@/Common/Models/Patient/UI/Profile/IPatientCliniciansModel";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { ConsultantSearch } from "./ConsultantSearch";
import { ConsultantSelection } from "./ConsultantSelection";
import { IConsultantProps } from "./IConsultantProps";

interface IAddOrEditConsultantPopupProps {
    patientClinicianModel: IPatientCliniciansModel;
    intervenor: PatientRefIntervenorWithDetailsModel;
    patientContactId: string;
    employees: Immutable.List<SearchableItemModelT<any>>;
    redx: React.CSSProperties;
    onEditConsultant: (intervenor: PatientRefIntervenorWithDetailsModel) => void;
    onAddConsultant: (intervenor: PatientRefIntervenorWithDetailsModel, isAddPlus: boolean) => void;
    onClose: () => void;
    isEditMode?: boolean;
}

function AddOrEditConsultantPopup(props: IAddOrEditConsultantPopupProps): JSX.Element {
    const { getText } = useTranslation();

    const isEditMode = useMemo(
        () => props.isEditMode && !IsNullOrWhiteSpace(props.intervenor.GetDoctorId()),
        [props.isEditMode, props.intervenor]
    );

    const [intervenor, setIntervenor] = usePartialState<PatientRefIntervenorWithDetailsModel>(
        cloneIntervenor(props.intervenor),
        intervenor => cloneIntervenor(intervenor)
    );

    const isModified = useMemo(
        () => isEditMode && !ObjectHelper.equals(props.intervenor, intervenor, true),
        [isEditMode, props.intervenor, intervenor]
    );

    const searchConsultant = !intervenor.Model.HealthCareProvider && !intervenor.Model.LicencedIntervenor && !intervenor.Model.Intervenor.EmployeeId;

    const title = isEditMode
        ? getText("EditStaffMembersExternalLabel")
        : getText(intervenor?.SearchByEmployee ? "AddStaffMembersInternalTitleLabel" : "AddStaffMembersExternalTitleLabel");

    const onClearIntervenorSelection = (): void => {
        const newIntervenor = cloneIntervenor(intervenor);
        newIntervenor.DeleteIntervenorSelection(true);
        setIntervenor(newIntervenor);
    };

    const commonProps: IConsultantProps = {
        patientClinicianModel: props.patientClinicianModel,
        intervenor,
        onHealthCareProviderSelect: (entity, isModification) => {
            const newIntervenor = cloneIntervenor(intervenor);
            const shouldKeepIntervenorId = true;
            const shouldKeepNote = isModification;
            newIntervenor.DeleteIntervenorSelection(shouldKeepIntervenorId, shouldKeepNote);
            newIntervenor.updateTitleAndSpecialtyFromHealthCareProvider(entity);
            newIntervenor.Model.HealthCareProvider = entity;
            newIntervenor.Model.Intervenor.DirectoryFaxNumberId = entity
                .getAllFaxNumbers()
                .firstPreferredOrDefault(p => p.id === intervenor.Model.Intervenor.DirectoryFaxNumberId)
                .id;
            setIntervenor(newIntervenor);
        }
    };

    return (
        <Modal
            showOverlay
            showX
            draggable
            title={title}
            onClose={() => props.onClose()}
            style={{ overflow: "visible" }}
            bodyStyle={{ overflow: "visible" }}
            fixedOverlayPosition
        >
            <div className="p-body margin-none-right margin-none-left" key="_cat_Clinic_Edit_Add">
                {searchConsultant
                    ? (
                        <ConsultantSearch
                            {...commonProps}
                            patientContactId={props.patientContactId}
                            employees={props.employees}
                            onDoctorEmployeeSelect={employee => {
                                const newIntervenor = cloneIntervenor(intervenor);
                                newIntervenor.DeleteIntervenorSelection(true);
                                newIntervenor.Model.Intervenor.EmployeeId = employee.ContactFacetId;
                                newIntervenor.InitEmployee();
                                setIntervenor(newIntervenor);
                            }}
                            onDoctorSelect={licensedIntervenor => {
                                const newIntervenor = cloneIntervenor(intervenor);
                                newIntervenor.DeleteIntervenorSelection(true);
                                newIntervenor.Model.LicencedIntervenor = licensedIntervenor;
                                newIntervenor.updateTitleAndSpecialtyFromLicencedintervenor(licensedIntervenor);
                                setIntervenor(newIntervenor);
                            }}
                            isInternal={intervenor.SearchByEmployee}
                        />
                    )
                    : (
                        <ConsultantSelection
                            {...commonProps}
                            redx={props.redx}
                            onSpecialtySelect={titleAndSpecialty => {
                                const newIntervenor = cloneIntervenor(intervenor);
                                setIntervenor(newIntervenor.FromSpecialityResult(titleAndSpecialty));
                            }}
                            onClearSpecialtySelection={() => {
                                const newIntervenor = cloneIntervenor(intervenor);
                                newIntervenor.DeleteSpeciality();
                                setIntervenor(newIntervenor);
                            }}
                            onClearIntervenorSelection={() => onClearIntervenorSelection()}
                            onSelectDirectoryFaxNumber={id => {
                                const newIntervenor = cloneIntervenor(intervenor);
                                newIntervenor.Model.Intervenor.DirectoryFaxNumberId = id;
                                setIntervenor(newIntervenor);
                            }}
                            onNoteChange={note => {
                                const newIntervenor = cloneIntervenor(intervenor);
                                newIntervenor.Model.Intervenor.Note = note;
                                setIntervenor(newIntervenor);
                            }}
                        />
                    )
                }
            </div>
            <div className="p-footer margin-md-top">
                <div className="text-right">
                    <button className="btn btn-default push-right-5" onClick={() => props.onClose()}>{getText("CancelLabel")}</button>
                    {isEditMode
                        ? (
                            <button
                                className="btn btn-primary push-right-5"
                                disabled={!intervenor.IsValid() || !isModified}
                                onClick={() => {
                                    props.onEditConsultant(intervenor);
                                    props.onClose();
                                }}
                            >
                                {getText("SaveLabel")}
                            </button>
                        )
                        : (
                            <>
                                <button
                                    className="btn btn-primary push-right-5"
                                    disabled={!intervenor.IsValid()}
                                    onClick={() => props.onAddConsultant(intervenor, false)}
                                    data-cy="consultant-add"
                                >
                                    {getText("AddLabel")}
                                </button>
                                <button
                                    className="btn btn-primary"
                                    disabled={!intervenor.IsValid()}
                                    onClick={() => {
                                        props.onAddConsultant(intervenor, true);
                                        setIntervenor(PatientRefIntervenorWithDetailsModel.New(intervenor.SearchByEmployee));
                                    }}
                                >
                                    {getText("AddLabel") + " [+]"}
                                </button>
                            </>
                        )
                    }
                </div>
            </div>
        </Modal>
    );
}

function cloneIntervenor(intervenor: Partial<PatientRefIntervenorWithDetailsModel>): PatientRefIntervenorWithDetailsModel {
    const model = intervenor.Model ? new PatientRefIntervenorWithDetails(intervenor.Model) : null;

    return new PatientRefIntervenorWithDetailsModel(model, intervenor.IsEditMode, intervenor.SearchByEmployee);
}

export { AddOrEditConsultantPopup };
